import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { broadcastLogin, broadcastLogout } from "@/lib/authSync";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("rtg_token");
    const cached = localStorage.getItem("rtg_driver");
    if (token && cached) {
      setDriver(JSON.parse(cached));
      api.get("/auth/me").catch((err) => console.warn("auth/me refresh failed", err?.response?.status)).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
    // v2.7.63+E46.5 · Listen for cross-tab login/logout broadcasts so
    // opening a new browser window on Screen 2 immediately picks up the
    // token from Screen 1 without a re-login prompt.
    const handleStorage = (ev) => {
      if (ev.key === "rtg_token") {
        if (ev.newValue) {
          const cached2 = localStorage.getItem("rtg_driver");
          if (cached2) {
            try { setDriver(JSON.parse(cached2)); } catch (_e) { /* noop */ }
          }
        } else {
          setDriver(null);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    // v2.7.65+E151 · Same-tab hook — BroadcastChannel writes don't fire
    // a storage event in the receiving tab, so authSync also emits a
    // custom event we listen to here for immediate React state refresh.
    const handleSameTab = (ev) => {
      const d = ev?.detail || {};
      if (d.type === "login") {
        const cached2 = localStorage.getItem("rtg_driver");
        if (cached2) {
          try { setDriver(JSON.parse(cached2)); } catch (_e) { /* noop */ }
        }
      } else if (d.type === "logout") {
        setDriver(null);
      }
    };
    window.addEventListener("rtg-auth-updated:driver", handleSameTab);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("rtg-auth-updated:driver", handleSameTab);
    };
  }, []);

  const persist = useCallback((data) => {
    localStorage.setItem("rtg_token", data.access_token);
    localStorage.setItem("rtg_driver", JSON.stringify(data.driver));
    setDriver(data.driver);
    // v2.7.63+E46.5 · broadcast to sibling tabs
    broadcastLogin("driver", { token: data.access_token, user: data.driver });
  }, []);

  const loginEmail = useCallback(async (email, password, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember: !!remember });
    persist(data);
    return data;
  }, [persist]);
  const loginPin = useCallback(async (pin) => {
    const { data } = await api.post("/auth/login-pin", { pin });
    persist(data);
    return data;
  }, [persist]);
  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    persist(data);
    return data;
  }, [persist]);
  const logout = useCallback(() => {
    localStorage.removeItem("rtg_token");
    localStorage.removeItem("rtg_driver");
    setDriver(null);
    broadcastLogout("driver");
  }, []);

  const value = useMemo(
    () => ({ driver, loading, loginEmail, loginPin, register, logout }),
    [driver, loading, loginEmail, loginPin, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
